import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, Mail, Phone, Ban } from 'lucide-react';
import { Button } from '../shared';
import StripeConnectStatusBadge from '../shared/StripeConnectStatusBadge';

const VendorHeader = ({ vendor, vendorProfile, onVerify, onReject, onSuspend }) => {
  const navigate = useNavigate();
  const isVerified = vendorProfile?.is_verified;
  const isActive = vendor?.is_active !== false && vendorProfile?.is_active !== false;

  const getVendorName = () => {
    if (vendorProfile?.business_name) return vendorProfile.business_name;
    if (vendor?.first_name && vendor?.last_name) return `${vendor.first_name} ${vendor.last_name}`;
    if (vendor?.name) return vendor.name;
    if (vendor?.email) return vendor.email.split('@')[0];
    return 'Unknown Vendor';
  };

  const suspensionReason = vendorProfile?.suspension_reason || vendorProfile?.suspensionReason;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5">
      {/* Back link at top */}
      <button
        onClick={() => navigate('/admin/vendors')}
        className="flex items-center text-sm text-gray-500 hover:text-gray-900 transition-colors mb-4"
        aria-label="Back to vendors"
      >
        <ArrowLeft className="w-4 h-4 mr-1" />
        <span className="hidden sm:inline">Back to Vendors</span>
      </button>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        {/* Left: Info */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
              {getVendorName()}
            </h1>
            {isVerified && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">
                <CheckCircle className="w-3 h-3" />
                Verified
              </span>
            )}
            {!isActive && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">
                <Ban className="w-3 h-3" />
                Suspended
              </span>
            )}
            <StripeConnectStatusBadge connect={vendorProfile?.stripe_connect} />
          </div>
          {!isActive && suspensionReason && (
            <p className="text-sm text-red-600 mt-1 mb-2">
              <span className="font-medium">Reason:</span> {suspensionReason}
            </p>
          )}
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-sm text-gray-500">
              {vendor?.email && (
                <span className="flex items-center gap-1 truncate">
                  <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                  {vendor.email}
                </span>
              )}
              {vendor?.phone && (
                <span className="flex items-center gap-1">
                  <Phone className="w-3.5 h-3.5 flex-shrink-0" />
                  {vendor.phone}
                </span>
              )}
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
          {!isVerified && (
            <Button
              onClick={onVerify}
              variant="outline"
              size="sm"
              icon={CheckCircle}
              className="border-green-600 text-green-600 hover:bg-green-50 text-xs sm:text-sm"
            >
              Verify
            </Button>
          )}
          {!isVerified && (
            <Button
              onClick={onReject}
              variant="outline"
              size="sm"
              icon={XCircle}
              className="border-red-600 text-red-600 hover:bg-red-50 text-xs sm:text-sm"
            >
              Reject
            </Button>
          )}
          <Button
            onClick={onSuspend}
            variant="outline"
            size="sm"
            icon={Ban}
            className={isActive ? "border-amber-600 text-amber-600 hover:bg-amber-50" : "border-green-600 text-green-600 hover:bg-green-50 text-xs sm:text-sm"}
          >
            {isActive ? 'Suspend' : 'Reactivate'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VendorHeader;
